import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { hostApiClient } from '../hostApiClient';
import { tutorApiClient } from '../tutorApiClient';
import { userApiClient } from '../userApiClient';
import { TUTOR_BOOKING_NEEDS_ACTION_COUNT_CHANGED_EVENT } from '../utils/tutorBookingNeedsActionCount';
import { getNeedsActionCountFromResponse } from '../utils/tutorBookingNeedsActionCountHelpers';

export type TutorBookingNeedsActionScope = 'user' | 'tutor' | 'host';

/**
 * Lightweight needs-action badge total for nav / compact counters.
 * Prefer /summary on booking list pages that need the full tab count bundle.
 */
export function useTutorBookingNeedsActionCount(
  scope: TutorBookingNeedsActionScope | null,
): number {
  const location = useLocation();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!scope) {
      setCount(0);
      return;
    }

    try {
      const response =
        scope === 'user'
          ? await userApiClient.getTutorBookingsNeedsUserActionCount()
          : scope === 'tutor'
            ? await tutorApiClient.getTutorBookingsNeedsTutorActionCount()
            : await hostApiClient.getHostTutorBookingsNeedsTutorActionCount();
      setCount(getNeedsActionCountFromResponse(response));
    } catch {
      // Nav badge is non-critical; keep the last known count on transient errors.
    }
  }, [scope]);

  useEffect(() => {
    void refresh();
  }, [refresh, location.pathname]);

  useEffect(() => {
    if (!scope) {
      return;
    }

    const handleChange = () => {
      void refresh();
    };

    window.addEventListener(TUTOR_BOOKING_NEEDS_ACTION_COUNT_CHANGED_EVENT, handleChange);
    return () => window.removeEventListener(TUTOR_BOOKING_NEEDS_ACTION_COUNT_CHANGED_EVENT, handleChange);
  }, [scope, refresh]);

  return count;
}
